import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@/components/ui';
import { Badge } from '@/components/ui/badge';
import { TransactionStatusBadge } from './status-badge';
import { formatDate, formatCents, truncate } from '@/lib/utils';
import { MerchantCell } from '@/components/shared/merchant-cell';
import type { SortState } from '@/hooks/use-sort-state';
import type { AdminTransactionSummary } from '@/types/admin-transaction';

/** Sortierbare Spalten der Transaction-Tabelle */
export type TransactionSortColumn = 'createdAt' | 'status' | 'grossAmount';

interface TransactionTableProps {
  transactions: AdminTransactionSummary[];
  onRowClick: (transactionId: string, merchantId?: string) => void;
  sort?: SortState<TransactionSortColumn>;
  onToggleSort?: (column: TransactionSortColumn) => void;
}

export function TransactionTable({
  transactions,
  onRowClick,
  sort,
  onToggleSort,
}: TransactionTableProps) {
  const { t } = useTranslation('transactions');

  if (transactions.length === 0) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        {t('list.empty')}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('list.columns.transactionId')}</TableHead>
          <TableHead>{t('list.columns.merchant')}</TableHead>
          <TableHead>{t('list.columns.service')}</TableHead>
          <SortableHead
            label={t('list.columns.status')}
            column="status"
            sort={sort}
            onToggle={onToggleSort}
          />
          <TableHead>{t('list.columns.flow')}</TableHead>
          <TableHead>{t('list.columns.mode')}</TableHead>
          <SortableHead
            label={t('list.columns.amount')}
            column="grossAmount"
            sort={sort}
            onToggle={onToggleSort}
          />
          <SortableHead
            label={t('list.columns.created')}
            column="createdAt"
            sort={sort}
            onToggle={onToggleSort}
          />
        </TableRow>
      </TableHeader>
      <TableBody>
        {transactions.map((tx) => (
          <TableRow
            key={tx.transactionId}
            className="cursor-pointer hover:bg-muted/50"
            onClick={() => onRowClick(tx.transactionId, tx.merchantId)}
          >
            <TableCell className="font-mono text-sm">
              {truncate(tx.transactionId, 16)}
            </TableCell>
            <TableCell><MerchantCell merchantId={tx.merchantId} /></TableCell>
            <TableCell>
              <Link
                to={`/services/${tx.serviceId}`}
                className="font-mono text-sm text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {tx.serviceId}
              </Link>
            </TableCell>
            <TableCell>
              <TransactionStatusBadge status={tx.status} />
            </TableCell>
            <TableCell>{tx.flowType}</TableCell>
            <TableCell>
              <Badge variant={(tx.gatewayMode ?? 'live') === 'live' ? 'success' : 'warning'}>
                {(tx.gatewayMode ?? 'live') === 'live' ? 'LIVE' : 'MOCK'}
              </Badge>
            </TableCell>
            <TableCell>
              {tx.grossAmount != null ? formatCents(tx.grossAmount) : '-'}
            </TableCell>
            <TableCell>{formatDate(tx.createdAt)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Sortierbare Spaltenkoepfe mit Pfeil-Indikator */
function SortableHead<T extends string>({
  label,
  column,
  sort,
  onToggle,
}: {
  label: string;
  column: T;
  sort?: SortState<T>;
  onToggle?: (column: T) => void;
}) {
  const isActive = sort?.column === column;

  return (
    <TableHead
      className="cursor-pointer select-none hover:bg-muted/50"
      onClick={() => onToggle?.(column)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive && (
          sort.direction === 'asc'
            ? <ChevronUpIcon className="h-3.5 w-3.5" />
            : <ChevronDownIcon className="h-3.5 w-3.5" />
        )}
      </span>
    </TableHead>
  );
}
